import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { HttpErrorResponse, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';

import { AuthService } from '../services/auths';
import { tokenHeaderKey } from '../constants';
import { JwtPacketModel } from '../models/auths';

@Injectable()
export class AuthInterceptor implements HttpInterceptor, OnDestroy {
   private isRefreshing = false;
   private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

   constructor(private readonly authService: AuthService, private readonly router: Router) { }

   intercept(req: HttpRequest<any>, next: HttpHandler) {
      let authReq = req;
      const token = this.authService.currentAuthValue?.token;

      // Always add the token if it exists
      if (token) {
         authReq = this.addTokenHeader(req, token);
      }

      // Handle the request and catch errors
      return next.handle(authReq).pipe(
         catchError(error => {
            if (error instanceof HttpErrorResponse) {
               // Exclude login and refresh token endpoints from 401 handling
               if (!authReq.url.includes('/api/Auth/login') && !authReq.url.includes('/api/Auth/RefreshToken') && error.status === 401) {
                  return this.handle401Error(authReq, next);
               }

               if (error.status === 403) {
                  this.handle403Error();
                  return throwError(() => error); // Always rethrow errors after handling
               }

               if (error.status === 500) {
                  this.handle500Error();
                  return throwError(() => error); // Always rethrow errors after handling
               }
            }
            return throwError(() => error); // Rethrow any other error
         })
      );
   }

   private handle500Error() {
      this.router.navigate(['/error/500'], {
         queryParams: {},
      });
   }

   private handle403Error() {
      this.router.navigate(['/error/403'], {
         queryParams: {},
      });
   }

   private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
      if (!this.isRefreshing) {
         this.isRefreshing = true;
         this.refreshTokenSubject.next(null); // Clear the subject while refreshing

         const refershedToken = this.authService.currentAuthValue?.refershedToken;

         if (refershedToken) {
            return this.authService.refreshToken().pipe(
               switchMap((token: JwtPacketModel | undefined) => {
                  if (token?.token) {
                     this.isRefreshing = false;
                     this.refreshTokenSubject.next(token.token);
                     // Retry the original request with the new token
                     return next.handle(this.addTokenHeader(request, token.token));
                  } else {
                     // If refresh token failed or no new token, logout
                     this.authService.logout();
                     return throwError(() => new Error('Refresh token failed.')); // Throw an error to propagate
                  }
               }),
               catchError((err) => {
                  this.isRefreshing = false;
                  this.authService.logout(); // Logout on refresh error
                  return throwError(() => err); // Rethrow the error
               })
            );
         } else {
            // No refresh token available, logout the user
            this.isRefreshing = false;
            this.authService.logout();
            return throwError(() => new Error('No refresh token available.'));
         }
      } else {
         // If a refresh is already in progress, wait for the new token
         return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap((token) => next.handle(this.addTokenHeader(request, token))),
            catchError((err) => {
                // If an error occurs while waiting for the token, rethrow it
                return throwError(() => err);
            })
         );
      }
   }

   private addTokenHeader(request: HttpRequest<any>, token: string) {
      return request.clone({ headers: request.headers.set(tokenHeaderKey, 'Bearer ' + token) });
   }

   ngOnDestroy() {
      // Unsubscribe from refreshTokenSubject to prevent memory leaks
      this.refreshTokenSubject.complete();
   }
}